import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route <head> management for the SPA.
 *
 * WHY: every /app/* route used to ship the identical index.html: one shared
 * title, no canonical, no robots meta. To Googlebot the ~600 discovered
 * /app/players/sl##### URLs were byte-identical duplicates, so GSC filled
 * with "Duplicate without user-selected canonical" and Google picked
 * arbitrary winners (sl13423 canonicalized to sl13411). Report: 38 pages,
 * 2026-08-08 coverage export.
 *
 * Googlebot renders JS, so directives injected here are honored:
 * - Free research routes: unique title + self-canonical, indexable. (The
 *   long-term fix for these is prerendering; the crawlable content today
 *   lives on /draft-rankings/ and /rookie-rankings/.)
 * - Everything else (player shells, gated tools, auth/account plumbing):
 *   noindex,follow. To a crawler these render nothing useful, and an
 *   explicit directive beats letting Google guess at duplicates.
 *
 * Titles: computed season year, never typed (standing rule; pages shipped
 * "2025-26" deep into the 2026 draft season before this was enforced).
 *
 * DESCRIPTIONS (added 2026-08-27): this component set the title but left
 * index.html's single description on every route, so an audit crawl reported
 * /app/drafts and /app/rankings as duplicate-description pages. Each rule now
 * carries its own; routes without one fall back to the shell's, which is
 * correct for the home route it was written for.
 *
 * LIMIT, stated plainly: everything here runs in the browser. Googlebot
 * renders JS so it sees these values, but a non-rendering crawler (most SEO
 * audit tools, default Screaming Frog) only ever sees index.html's shell tags
 * and will keep reporting duplicates. That is cosmetic rather than harmful,
 * because every route it can reach is either noindex or canonicalized to a
 * server-rendered page. Prerendering is the only real fix for non-JS
 * crawlers.
 */

const now = new Date();
const SEASON_YEAR = now.getMonth() + 1 >= 2 ? now.getFullYear() : now.getFullYear() - 1;

const ORIGIN = 'https://fantasy.draftedge.com';
const BASE = '/app';

type Rule = { title: string; index: boolean; canonical?: string; desc?: string };

/** First match wins; longest prefixes first. */
const RULES: Array<[RegExp, Rule]> = [
  // Cross-canonical: these two routes carry the same rankings the
  // server-rendered SEO pages publish, and two indexable copies of one
  // dataset cannibalize each other. The static pages are the ones with
  // crawlable content, so they get the equity.
  [/^\/rankings/, { title: `${SEASON_YEAR} Fantasy Football Rankings | DraftEdge App`, index: true, canonical: `${ORIGIN}/draft-rankings/`,
    desc: `Full ${SEASON_YEAR} fantasy football rankings with tiers, bye weeks, and positional scarcity. Sort, filter, and build your own board.` }],
  [/^\/rookies/, { title: `${SEASON_YEAR} Rookie Rankings | DraftEdge App`, index: true, canonical: `${ORIGIN}/rookie-rankings/`,
    desc: `${SEASON_YEAR} rookie rankings for redraft and dynasty leagues, with landing spots and projected roles.` }],
  [/^\/trending/, { title: 'Waiver Wire Trends | DraftEdge App', index: true,
    desc: 'The most added and dropped players across fantasy leagues right now, so you see waiver moves as they happen.' }],
  [/^\/depth-charts/, { title: 'NFL Depth Charts | DraftEdge App', index: true,
    desc: 'Every NFL team depth chart in one place: starters, backups, and the role changes that move fantasy value.' }],
  [/^\/injuries/, { title: 'NFL Injury Report | DraftEdge App', index: true,
    desc: 'Current NFL injury designations and what each one means for your lineup decisions this week.' }],
  [/^\/news\/.+/, { title: 'NFL News | DraftEdge App', index: false,
    desc: 'NFL news and fantasy analysis from the DraftEdge draft room.' }],
  [/^\/news/, { title: 'NFL News | DraftEdge App', index: true,
    desc: 'NFL news and fantasy analysis from the DraftEdge draft room.' }],
  [/^\/players\//, { title: 'Player Profile | DraftEdge App', index: false,
    desc: 'Player profile with projections, rankings context, and draft value inside the DraftEdge app.' }],
  [/^\/drafts/, { title: 'Draft Room | DraftEdge App', index: false,
    desc: 'The live draft room: AI Draft Coach guidance on every pick, your cheat sheet, and Sleeper draft sync.' }],
  [/^\/cheatsheet/, { title: 'Cheat Sheets | DraftEdge App', index: false,
    desc: 'Build custom tiered cheat sheets: drag to rank, flag targets, and take your board into the draft.' }],
  [/^\/mocks/, { title: 'Mock Drafts | DraftEdge App', index: false,
    desc: 'Mock draft your real league settings against the CPU and practice every pick before draft night.' }],
  [/^\/compare/, { title: 'Compare Players | DraftEdge App', index: false,
    desc: 'Compare fantasy players side by side on projections, rankings, and draft cost.' }],
  [/^\/exposure/, { title: 'Exposure Report | DraftEdge App', index: false,
    desc: 'See which players you are overweight and underweight across all of your drafted teams.' }],
  [/^\/dashboard/, { title: 'Dashboard | DraftEdge App', index: false,
    desc: 'Your DraftEdge dashboard: leagues, drafts, and saved boards.' }],
  [/^\/login/, { title: 'Sign In | DraftEdge App', index: false,
    desc: 'Sign in to your DraftEdge account.' }],
  [/^\/register/, { title: 'Create Account | DraftEdge App', index: false,
    desc: 'Create a DraftEdge account to use the draft room, cheat sheets, and AI Draft Coach.' }],
  [/^\/$/, {
    title: `DraftEdge Draft Room: ${SEASON_YEAR} NFL Fantasy Draft Coach & Cheat Sheets`,
    index: true,
  }],
];

const FALLBACK: Rule = { title: 'DraftEdge App', index: false };

function upsert<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attr: string,
  value: string,
  marker: string
): HTMLElementTagNameMap[K] {
  let el = document.head.querySelector<HTMLElementTagNameMap[K]>(`${tag}[data-route-seo="${marker}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute('data-route-seo', marker);
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  return el;
}

function remove(marker: string) {
  document.head.querySelector(`[data-route-seo="${marker}"]`)?.remove();
}

/**
 * Rewrite the SHELL's description in place rather than appending a second
 * one. index.html already ships <meta name="description">, and a page
 * carrying two of them is a worse audit finding than the duplicate we are
 * fixing. The shell's original text is captured once so a route without its
 * own description restores it instead of inheriting the previous route's.
 */
const SHELL_DESC = (
  document.head.querySelector('meta[name="description"]') as HTMLMetaElement | null
)?.content ?? '';

function setDescription(text: string) {
  let el = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'description');
    document.head.appendChild(el);
  }
  el.setAttribute('content', text);
  // og:description tracks it so shared links match the page they open.
  const og = document.head.querySelector<HTMLMetaElement>('meta[property="og:description"]');
  if (og) {
    og.setAttribute('content', text);
  }
}

export function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const rule = RULES.find(([re]) => re.test(pathname))?.[1] ?? FALLBACK;

    document.title = rule.title;
    setDescription(rule.desc ?? SHELL_DESC);

    // og:title tracks the route too, for the same reason as og:description.
    const ogTitle = document.head.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', rule.title);
    }

    if (rule.index) {
      remove('robots');
      const canon = upsert('link', 'rel', 'canonical', 'canonical');
      canon.setAttribute('rel', 'canonical');
      canon.setAttribute(
        'href',
        rule.canonical ?? ORIGIN + BASE + (pathname === '/' ? '/' : pathname)
      );
    } else {
      remove('canonical');
      const robots = upsert('meta', 'name', 'robots', 'robots');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex, follow');
    }
  }, [pathname]);

  return null;
}
